// src/api/context.ts
/**
 * Frontend API client for account business context (profile + documents +
 * events).
 *
 * The business profile is the always-on frame: a handful of facts about the
 * company that every analysis is read against. Context documents are free-text
 * knowledge the agent can retrieve (playbooks, definitions, briefings). Context
 * events are dated happenings (campaign launches, site changes, price changes)
 * the agent uses to explain metric movements.
 *
 * All routes are account-scoped and proxied by the BFF under /admin/context.
 */

import { apiJson, apiData, apiNoContent } from "./client";

// ============================================================================
// Business profile ("Your business")
// ============================================================================

/** Who the business sells to. */
export type BusinessCustomerType = "b2c" | "b2b" | "both";

/**
 * The account's company profile.
 *
 * `industry_options` is the canonical industry list, served by the backend: a
 * Python constant cannot cross into TSX, so the Select renders from the
 * response instead of from a second copy that could drift. `industry` may hold
 * a legacy value outside that list (the column predates the validation) — show
 * it rather than blanking it.
 */
export type BusinessProfile = {
  industry: string | null;
  description: string | null;
  business_model: string | null;
  customer_type: BusinessCustomerType | null;
  markets: string[];
  seasonality: string | null;
  goals: string | null;
  competitors: string[];
  updated_at: string | null;
  industry_options: string[];
};

/**
 * The settings form's payload: every field travels, and null/[] clears it.
 *
 * The route is a PARTIAL update — an omitted key is left alone, an explicit
 * null clears. The form always holds the complete state, so it sends
 * everything and behaves as a full replace. Anything that changes only some
 * fields (the assistant's proposal cards) uses `BusinessProfilePatch`, which
 * is what keeps a stale card from reverting edits it never mentioned.
 */
export type BusinessProfileUpdate = {
  industry: string | null;
  description: string | null;
  business_model: string | null;
  customer_type: BusinessCustomerType | null;
  markets: string[];
  seasonality: string | null;
  goals: string | null;
  competitors: string[];
};

/**
 * Change some fields and leave the rest alone.
 *
 * Callers that hold only part of the state (the assistant's proposal cards)
 * MUST use this rather than padding the missing keys with null — padding is
 * how a card confirmed a day later silently reverts seven fields it never
 * mentioned. Keys present here are written; keys absent are untouched.
 */
export type BusinessProfilePatch = Partial<BusinessProfileUpdate>;

/** Server-side limits, mirrored here so the form can guard before submitting. */
export const BUSINESS_PROFILE_DESCRIPTION_MAX = 500;
export const BUSINESS_PROFILE_BUSINESS_MODEL_MAX = 150;
export const BUSINESS_PROFILE_SEASONALITY_MAX = 300;
export const BUSINESS_PROFILE_GOALS_MAX = 500;
export const BUSINESS_PROFILE_MARKETS_MAX = 12;
export const BUSINESS_PROFILE_MARKET_MAX = 40;
export const BUSINESS_PROFILE_COMPETITORS_MAX = 8;
export const BUSINESS_PROFILE_COMPETITOR_MAX = 40;

/**
 * Does this read as a plain company name?
 *
 * Competitor names are the only customer input licensed into an outbound web
 * search, so the backend rejects addresses, links and letterless strings with a
 * 422. Mirrored here to explain the rule inline instead of after a round trip.
 */
export function isCompanyName(value: string): boolean {
  const lowered = value.toLowerCase();
  if (["@", "://", "www."].some((bad) => lowered.includes(bad))) return false;
  return /\p{L}/u.test(value);
}

/** Get the account's business profile. Returns empty fields when unset. */
export async function getBusinessProfile(): Promise<BusinessProfile> {
  return apiData<BusinessProfile>("/admin/context/business-profile");
}

/** Replace the business profile. Returns the stored (cleaned) profile. */
export async function updateBusinessProfile(
  data: BusinessProfilePatch
): Promise<BusinessProfile> {
  return apiJson<BusinessProfile>("/admin/context/business-profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Documents
// ============================================================================

export type EmbeddingStatus = "pending" | "ready" | "error";

/** Result of the last Google Drive sync. Null/absent on manual documents. */
export type ContextSyncStatus = "ok" | "error";

/** `source_type` of a document imported from Google Drive. */
export const GOOGLE_DRIVE_SOURCE_TYPE = "google_drive";

export type ContextDocument = {
  public_id: string;
  title: string;
  /** Agent-facing routing hint: what this covers and when to consult it. */
  description: string | null;
  content_preview: string;
  agent_key: string | null;
  is_pinned: boolean;
  is_enabled: boolean;
  embedding_status: EmbeddingStatus;
  embedding_error: string | null;
  /** "manual" or "google_drive". */
  source_type: string;
  // ---- Google Drive documents only (absent/null on manual documents) ----
  last_synced_at?: string | null;
  sync_status?: ContextSyncStatus | null;
  sync_error?: string | null;
  drive_file_id?: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/** True when the document's body is owned by Google Drive (read-only here). */
export function isDriveDocument(doc: {
  source_type: string;
}): boolean {
  return doc.source_type === GOOGLE_DRIVE_SOURCE_TYPE;
}

/** List item + the full document body (detail routes only). */
export type ContextDocumentDetail = ContextDocument & {
  content: string;
};

export type CreateContextDocumentRequest = {
  title: string;
  description?: string | null;
  content: string;
  agent_key?: string | null;
  is_pinned?: boolean;
  is_enabled?: boolean;
};

export type UpdateContextDocumentRequest = {
  title?: string;
  /** Must be non-empty — clearing travels via `clear_description`. */
  description?: string;
  /** Send true to remove the description. */
  clear_description?: boolean;
  content?: string;
  agent_key?: string | null;
  /** Send true to reset the agent scope back to "all agents". */
  clear_agent_key?: boolean;
  is_pinned?: boolean;
  is_enabled?: boolean;
};

/** Server-side limits, mirrored here so the form can guard before submitting. */
export const CONTEXT_DOCUMENT_TITLE_MAX = 200;
export const CONTEXT_DOCUMENT_DESCRIPTION_MAX = 500;
export const CONTEXT_DOCUMENT_CONTENT_MAX = 100000;

/** List all context documents for the current account. */
export async function listContextDocuments(): Promise<ContextDocument[]> {
  return apiData<ContextDocument[]>("/admin/context/documents");
}

/** Get a single context document, including its full content. */
export async function getContextDocument(
  publicId: string
): Promise<ContextDocumentDetail> {
  return apiData<ContextDocumentDetail>(
    `/admin/context/documents/${encodeURIComponent(publicId)}`
  );
}

/** Create a context document. Indexing runs server-side after the write. */
export async function createContextDocument(
  data: CreateContextDocumentRequest
): Promise<ContextDocumentDetail> {
  return apiJson<ContextDocumentDetail>("/admin/context/documents", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Update a context document. Re-saving also retries a failed indexing run. */
export async function updateContextDocument(
  publicId: string,
  data: UpdateContextDocumentRequest
): Promise<ContextDocumentDetail> {
  return apiJson<ContextDocumentDetail>(
    `/admin/context/documents/${encodeURIComponent(publicId)}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

/** Delete a context document. */
export async function deleteContextDocument(publicId: string): Promise<void> {
  await apiNoContent(
    `/admin/context/documents/${encodeURIComponent(publicId)}`,
    { method: "DELETE" }
  );
}

/**
 * Ask the backend to propose the agent-facing description for a document.
 *
 * Stateless: send the title/content currently on screen — this works before
 * the document exists. The proposal is never stored; the caller decides
 * whether to keep it.
 */
export async function suggestContextDocumentDescription(
  title: string,
  content: string
): Promise<string> {
  const data = await apiJson<{ description: string }>(
    "/admin/context/documents/suggest-description",
    { method: "POST", body: JSON.stringify({ title, content }) }
  );
  return data.description;
}

/**
 * Re-fetch a Google Drive document's body now and re-index it.
 *
 * Returns the refreshed document (including `sync_status` / `sync_error`) —
 * a failed fetch comes back as a 200 with `sync_status: "error"`, not a throw,
 * so the row can show what went wrong.
 */
export async function syncContextDocument(
  publicId: string
): Promise<ContextDocumentDetail> {
  return apiJson<ContextDocumentDetail>(
    `/admin/context/documents/${encodeURIComponent(publicId)}/sync`,
    { method: "POST" }
  );
}

// ============================================================================
// Google Drive import (ADR-0139)
// ============================================================================

/**
 * Picker feature-detection + public client config.
 *
 * `configured: false` means the deployment has no Picker credentials — the
 * UI hides the Drive entry point entirely instead of offering a dead button.
 * Nothing here is a secret: the API key is referrer-restricted and the client
 * id already ships in the login bundle.
 */
export type DriveConfig = {
  configured: boolean;
  api_key?: string;
  app_id?: string;
  client_id?: string;
};

export type DriveStatus = {
  connected: boolean;
  authorization_public_id?: string;
};

export type DrivePickerToken = {
  access_token: string;
  expires_in: number;
  authorization_public_id: string;
};

export type DriveImportFileStatus = "imported" | "already_imported" | "error";

export type DriveImportFileResult = {
  file_id: string;
  status: DriveImportFileStatus;
  public_id?: string;
  error?: string;
};

/** Per-file outcomes; the call itself succeeds even when files failed. */
export type DriveImportResponse = {
  results: DriveImportFileResult[];
};

export type DriveImportRequest = {
  authorization_public_id: string;
  /** Omit to import as "all agents" — scope can be changed per document after. */
  agent_key?: string | null;
  files: { file_id: string }[];
};

/** Is the Google Picker available in this deployment, and with which config? */
export async function getDriveConfig(): Promise<DriveConfig> {
  return apiData<DriveConfig>("/admin/context/drive/config");
}

/** Does this account have a Drive-context Google connection? */
export async function getDriveStatus(): Promise<DriveStatus> {
  return apiData<DriveStatus>("/admin/context/drive/status");
}

/**
 * Mint a short-lived Drive access token for one Picker session.
 *
 * The token is for the Picker only — keep it in memory, never persist it.
 */
export async function createDrivePickerToken(
  authorizationPublicId: string
): Promise<DrivePickerToken> {
  return apiJson<DrivePickerToken>("/admin/context/drive/picker-token", {
    method: "POST",
    body: JSON.stringify({ authorization_public_id: authorizationPublicId }),
  });
}

/** Import the picked Drive files as context documents. */
export async function importDriveFiles(
  data: DriveImportRequest
): Promise<DriveImportResponse> {
  return apiJson<DriveImportResponse>("/admin/context/drive/import", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Events
// ============================================================================

export type ContextEventCategory =
  | "campaign"
  | "product"
  | "website"
  | "pricing"
  | "meeting"
  | "external"
  | "incident"
  | "other";

/** Fixed set accepted by the backend, in the order shown in the UI. */
export const CONTEXT_EVENT_CATEGORIES: ContextEventCategory[] = [
  "campaign",
  "product",
  "website",
  "pricing",
  "meeting",
  "external",
  "incident",
  "other",
];

export type ContextEvent = {
  public_id: string;
  title: string;
  description: string | null;
  /** YYYY-MM-DD */
  event_date: string;
  /** YYYY-MM-DD, or null for a single-day event. */
  end_date: string | null;
  category: ContextEventCategory;
  source_type: string;
  created_at: string | null;
  updated_at: string | null;
};

export type CreateContextEventRequest = {
  title: string;
  description?: string | null;
  event_date: string;
  end_date?: string | null;
  category: ContextEventCategory;
};

export type UpdateContextEventRequest = {
  title?: string;
  description?: string | null;
  event_date?: string;
  end_date?: string | null;
  /** Send true to turn a multi-day event back into a single-day event. */
  clear_end_date?: boolean;
  category?: ContextEventCategory;
};

/** Server-side limits, mirrored here so the form can guard before submitting. */
export const CONTEXT_EVENT_TITLE_MAX = 120;
export const CONTEXT_EVENT_DESCRIPTION_MAX = 2000;

/** List all context events for the current account. */
export async function listContextEvents(): Promise<ContextEvent[]> {
  return apiData<ContextEvent[]>("/admin/context/events");
}

/** Create a context event. */
export async function createContextEvent(
  data: CreateContextEventRequest
): Promise<ContextEvent> {
  return apiJson<ContextEvent>("/admin/context/events", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Update a context event. */
export async function updateContextEvent(
  publicId: string,
  data: UpdateContextEventRequest
): Promise<ContextEvent> {
  return apiJson<ContextEvent>(
    `/admin/context/events/${encodeURIComponent(publicId)}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

/** Delete a context event. */
export async function deleteContextEvent(publicId: string): Promise<void> {
  await apiNoContent(`/admin/context/events/${encodeURIComponent(publicId)}`, {
    method: "DELETE",
  });
}
